// =============================================================================
// COMBINED STICKY ELEMENTS - HEADER & NAV
// =============================================================================

/**
 * Unified sticky functionality for header and navigation blocks
 * Handles coordination between sticky header and sticky nav blocks
 */

document.addEventListener('DOMContentLoaded', function() {
    // Initialize sticky header
    const header = document.querySelector('.header');
    const headerWrapper = document.querySelector('.header-wrapper');
    
    // Initialize sticky nav blocks
    const stickyNavBlocks = document.querySelectorAll('.sticky-nav-block');
    
    let lastScrollTop = 0;
    let ticking = false;
    let headerHeight = 0;
    let isHeaderSticky = false;
    
    // =============================================================================
    // STICKY HEADER FUNCTIONALITY
    // =============================================================================
    
    function updateStickyHeader(scrollTop) {
        if (!header || !headerWrapper) return;
        
        const headerTop = header.offsetTop;
        headerHeight = header.offsetHeight;
        
        // Add sticky class when scrolled down past the header
        if (scrollTop > headerTop) {
            if (!isHeaderSticky) {
                header.classList.add('is-sticky');
                headerWrapper.style.paddingTop = headerHeight + 'px';
                isHeaderSticky = true;
            }
        } else {
            if (isHeaderSticky) {
                header.classList.remove('is-sticky');
                headerWrapper.style.paddingTop = '0';
                isHeaderSticky = false;
            }
        }
    }
    
    // =============================================================================
    // STICKY NAV FUNCTIONALITY
    // =============================================================================
    
    // Initialize each sticky nav block
    const stickyNavInstances = [];
    
    stickyNavBlocks.forEach((block) => {
        const instance = initializeStickyNav(block);
        if (instance) {
            stickyNavInstances.push(instance);
        }
    });
    
    function initializeStickyNav(block) {
        const stickyNav = block.querySelector('.sticky-nav');
        const navLinks = block.querySelectorAll('.sticky-nav-link');
        const prototype = block.querySelector('.sticky-nav-prototype');
        
        if (!stickyNav || navLinks.length === 0 || !prototype) return null;
        
        let originalTop = null;
        let isFixed = false;
        
        // Function to get document-relative Y position
        function getDocTop(el) {
            const rect = el.getBoundingClientRect();
            return rect.top + (window.pageYOffset || document.documentElement.scrollTop);
        }
        
        // Function to get and store the original position using the prototype
        function storeOriginalPosition() {
            if (originalTop === null) {
                originalTop = getDocTop(prototype);
            }
            return originalTop;
        }
        
        // Function to manage placeholder height
        function setPlaceholderHeight(useHeight) {
            const newHeight = useHeight ? stickyNav.offsetHeight + 'px' : '0px';
            if (prototype.style.height !== newHeight) {
                prototype.style.height = newHeight;
            }
        }
        
        // Update sticky nav position based on scroll
        function updatePosition(scrollTop) {
            const stickyNavOriginalTop = storeOriginalPosition();
            const stickyNavRect = stickyNav.getBoundingClientRect();
            
            // Calculate the top offset based on sticky header
            const topOffset = isHeaderSticky ? headerHeight : 0;
            
            // Become fixed when we've scrolled past original top
            if (scrollTop > stickyNavOriginalTop - topOffset) {
                if (!isFixed) {
                    stickyNav.classList.add('sticky-fixed');
                    isFixed = true;
                }
                
                // Set top position to account for sticky header
                stickyNav.style.top = topOffset + 'px';
                
                // Keep placeholder height so content doesn't jump
                setPlaceholderHeight(true);
                
                // Optional "scrolled" styling threshold
                if (scrollTop > stickyNavOriginalTop + 100) {
                    stickyNav.classList.add('scrolled');
                } else {
                    stickyNav.classList.remove('scrolled');
                }
            } else {
                // Not fixed
                if (isFixed) {
                    stickyNav.classList.remove('sticky-fixed', 'scrolled');
                    stickyNav.style.top = '';
                    isFixed = false;
                }
                setPlaceholderHeight(false);
            }
        }
        
        // Update active nav item based on scroll position
        function updateActiveNav(scrollTop) {
            const sections = [];
            const navItems = [];
            
            navLinks.forEach(link => {
                const targetId = link.getAttribute('data-target');
                const targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                    // Account for sticky header + sticky nav when calculating section positions
                    const offset = isHeaderSticky ? headerHeight + stickyNav.offsetHeight : 100;
                    sections.push({
                        element: targetElement,
                        id: targetId,
                        top: targetElement.offsetTop - offset
                    });
                    navItems.push(link);
                }
            });
            
            let activeSection = null;
            
            // Find the current section
            for (let i = sections.length - 1; i >= 0; i--) {
                if (scrollTop >= sections[i].top) {
                    activeSection = sections[i];
                    break;
                }
            }
            
            // Update active states
            navItems.forEach(link => {
                link.classList.remove('active');
                if (activeSection && link.getAttribute('data-target') === activeSection.id) {
                    link.classList.add('active');
                }
            });
        }
        
        // Smooth scroll to target section
        function handleNavClick(e) {
            e.preventDefault();
            
            const targetId = e.target.getAttribute('data-target');
            const targetElement = document.getElementById(targetId);
            
            if (targetElement) {
                // Account for both sticky header and sticky nav
                const offset = isHeaderSticky ? headerHeight + stickyNav.offsetHeight : 80;
                const targetPosition = targetElement.offsetTop - offset;
                
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            }
        }
        
        // Handle window resize to recalculate positions
        function handleResize() {
            originalTop = null;
            if (isFixed) {
                stickyNav.classList.remove('sticky-fixed', 'scrolled');
                stickyNav.style.top = '';
                isFixed = false;
            }
            setPlaceholderHeight(false);
            setTimeout(() => {
                storeOriginalPosition();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                updatePosition(scrollTop);
                updateActiveNav(scrollTop);
            }, 100);
        }
        
        // Event listeners for nav clicks
        navLinks.forEach(link => {
            link.addEventListener('click', handleNavClick);
        });
        
        // Resize listener
        window.addEventListener('resize', handleResize, { passive: true });
        
        // Return instance methods for coordinated updates
        return {
            updatePosition,
            updateActiveNav,
            reset: function() {
                originalTop = null;
                if (isFixed) {
                    stickyNav.classList.remove('sticky-fixed', 'scrolled');
                    stickyNav.style.top = '';
                    isFixed = false;
                }
                setPlaceholderHeight(false);
            }
        };
    }
    
    // =============================================================================
    // COORDINATED SCROLL HANDLER
    // =============================================================================
    
    function updateAllStickyElements() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Update sticky header first (to get accurate headerHeight)
        updateStickyHeader(scrollTop);
        
        // Then update all sticky nav instances
        stickyNavInstances.forEach(instance => {
            instance.updatePosition(scrollTop);
            instance.updateActiveNav(scrollTop);
        });
        
        lastScrollTop = scrollTop;
        ticking = false;
    }
    
    function requestTick() {
        if (!ticking) {
            requestAnimationFrame(updateAllStickyElements);
            ticking = true;
        }
    }
    
    // Listen for scroll events
    window.addEventListener('scroll', requestTick, { passive: true });
    
    // Handle resize events for header
    window.addEventListener('resize', function() {
        if (header && header.classList.contains('is-sticky')) {
            headerHeight = header.offsetHeight;
            headerWrapper.style.paddingTop = headerHeight + 'px';
        }
    });
    
    // Initial setup
    setTimeout(() => {
        updateAllStickyElements();
    }, 100);
});

// =============================================================================
// UTILITY FUNCTIONS FOR DEBUGGING
// =============================================================================

const StickyElementsUtils = {
    // Reset all sticky elements
    resetAll: function() {
        const header = document.querySelector('.header');
        const headerWrapper = document.querySelector('.header-wrapper');
        const stickyNavs = document.querySelectorAll('.sticky-nav');
        
        if (header) {
            header.classList.remove('is-sticky');
        }
        if (headerWrapper) {
            headerWrapper.style.paddingTop = '0';
        }
        
        stickyNavs.forEach(nav => {
            nav.classList.remove('sticky-fixed', 'scrolled');
            nav.style.top = '';
        });
    },
    
    // Get debug info
    getDebugInfo: function() {
        const header = document.querySelector('.header');
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        return {
            scrollTop: scrollTop,
            header: {
                isSticky: header?.classList.contains('is-sticky'),
                height: header?.offsetHeight
            },
            stickyNavs: Array.from(document.querySelectorAll('.sticky-nav')).map(nav => ({
                isFixed: nav.classList.contains('sticky-fixed'),
                isScrolled: nav.classList.contains('scrolled'),
                top: nav.style.top
            }))
        };
    }
};

// Make utils available globally for debugging
window.StickyElementsUtils = StickyElementsUtils;

